import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, ArrowLeft, Download, Eye, Save, Package, X } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput, { normalizeDecimalInput } from '../components/DecimalInput';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import InvoiceTemplates from './InvoiceTemplates';

// Estado de cada registro VeriFactu, dicho en corriente.
const ESTADOS_VF = {
  pendiente: 'Pendiente de envío',
  firmado: 'Enviándose',
  error_red: 'Reintentando (fallo de conexión)',
  aceptado: 'Aceptado',
  aceptado_con_errores: 'Aceptado con errores',
  rechazado: 'Rechazado',
};

const InvoiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Crear como proforma: /invoices/new?proforma=1. Al editar, manda la BD.
  const creatingProforma = !id && searchParams.get('proforma') === '1';
  const [isProforma, setIsProforma] = useState(creatingProforma);
  // Rectificativa nueva: /invoices/new?rectifica=ID («Rectificar» en Salientes).
  const rectificaId = !id ? searchParams.get('rectifica') : null;
  const creatingRectificativa = !!rectificaId;
  const [isRectificativa, setIsRectificativa] = useState(creatingRectificativa);
  const esRectificativa = creatingRectificativa || isRectificativa;
  const [rectificada, setRectificada] = useState(null);     // la factura que corrige
  const [rectificativas, setRectificativas] = useState([]); // al editar: las que la corrigen
  const [registrosVf, setRegistrosVf] = useState([]);
  const [convertedAt, setConvertedAt] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [products, setProducts] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [defaultTemplateApplied, setDefaultTemplateApplied] = useState(false);
  const [defaultTaxRate, setDefaultTaxRate] = useState(21);
  const [defaultIrpfRate, setDefaultIrpfRate] = useState(15);
  const [customerSearch, setCustomerSearch] = useState('');
  const [budgetSearch, setBudgetSearch] = useState('');
  const [budgetCandidateId, setBudgetCandidateId] = useState('');
  const [loadedInvoiceNumber, setLoadedInvoiceNumber] = useState('');
  const [form, setForm] = useState({
    customerId: '',
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    lines: [{ productId: '', description: '', quantity: 1, unitPrice: 0, discountRate: 0, taxRate: 21 }],
    irpfRate: 15,
    notes: '',
    paymentMethod: 'transferencia',
    invoicePrefix: '',
    budgetId: '',
    budgetIds: [],
    isIssued: false,
    isAccounted: false,
    collectionStatus: 'pendiente',
    collectedAmount: 0,
    sentToCustomerAt: null,
    sentToAccountantAt: null,
    cancelledAt: null,
    rectificationType: 'R4',
    rectificationMode: 'I',
    rectificationReason: ''
  });
  const modal = useModal();

  // Con signo: solo las rectificativas dejan escribir negativos, y los importes
  // de las demás nunca lo llevan.
  const getDecimalValue = (value) => {
    const normalized = normalizeDecimalInput(value, { allowNegative: true });
    if (normalized === '') return 0;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getLineBase = (line) => {
    if (line.isTextOnly) return 0;
    const quantity = getDecimalValue(line.quantity);
    const unitPrice = getDecimalValue(line.unitPrice);
    const discountRate = getDecimalValue(line.discountRate);
    return quantity * unitPrice * (1 - discountRate / 100);
  };

  useEffect(() => {
    loadCustomers();
    loadBudgets();
    loadProducts();
    loadSettings();
    if (!id && !rectificaId) {
      loadAndApplyDefaultTemplate();
    }
    if (id) loadInvoice();
    if (rectificaId) loadRectificada();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSettings = async () => {
    try {
      const res = await api.get('/settings');
      const list = Array.isArray(res.data) ? res.data : [];
      const taxSetting = list.find(s => s.key === 'default_tax_rate');
      const irpfSetting = list.find(s => s.key === 'default_irpf_rate');
      const tax = taxSetting ? parseFloat(taxSetting.value) : 21;
      const irpf = irpfSetting ? parseFloat(irpfSetting.value) : 15;
      setDefaultTaxRate(tax);
      setDefaultIrpfRate(irpf);
      if (!id && !rectificaId) {
        setForm(prev => ({
          ...prev,
          lines: [{ productId: '', description: '', quantity: 1, unitPrice: 0, taxRate: tax }],
          irpfRate: irpf,
        }));
      }
    } catch (error) {
      console.error('Error cargando configuración de facturación:', error);
    }
  };

  const loadAndApplyDefaultTemplate = async () => {
    if (defaultTemplateApplied) return;
    try {
      const res = await api.get('/invoice-templates');
      const templates = Array.isArray(res.data) ? res.data : [];
      const defaultTemplate = templates.find((template) => template.isDefault === true);
      if (!defaultTemplate) return;
      const nextLines = defaultTemplate.lines?.map((line) => (
        line.isTextOnly === true
          ? { productId: '', description: line.description ?? '', isTextOnly: true, quantity: 0, unitPrice: 0, discountRate: 0, taxRate: 0 }
          : {
            productId: line.productId ? String(line.productId) : '',
            description: line.description ?? '',
            isTextOnly: false,
            quantity: Number(line.quantity ?? 1),
            unitPrice: Number(line.unitPrice ?? 0),
            taxRate: Number(line.taxRate ?? defaultTaxRate),
          }
      ));
      if (!nextLines || nextLines.length === 0) return;
      setForm((prev) => ({ ...prev, lines: nextLines }));
      setDefaultTemplateApplied(true);
    } catch (error) {
      modal.error('Error cargando plantilla predeterminada');
    }
  };

  // Conceptos de la factura original para la rectificativa. Por diferencias van
  // en negativo: de partida deshacen la factura entera y se deja solo lo que
  // cambia; por sustitución, tal cual: cómo queda la factura.
  const lineasDeLaOriginal = (original, metodo) => (original.lines || []).map((l) => (
    l.isTextOnly
      ? { productId: '', description: l.description, isTextOnly: true, quantity: 0, unitPrice: 0, discountRate: 0, taxRate: 0 }
      : {
        productId: l.productId ? String(l.productId) : '',
        description: l.description,
        servicePeriod: l.servicePeriod || '',
        isTextOnly: false,
        quantity: metodo === 'I' ? -parseFloat(l.quantity) : parseFloat(l.quantity),
        unitPrice: parseFloat(l.unitPrice),
        discountRate: parseFloat(l.discountRate ?? 0),
        taxRate: parseFloat(l.taxRate),
      }
  ));

  const numeroImpreso = (inv) => (inv.isRectificativa ? inv.invoiceNumber : String(inv.invoiceNumberSeq).padStart(3, '0'));

  const loadRectificada = async () => {
    try {
      const res = await api.get(`/invoices/${rectificaId}`);
      const orig = res.data;
      setRectificada(orig);
      const hoy = new Date();
      const plazo = Math.round((new Date(orig.dueDate) - new Date(orig.date)) / 86400000);
      const dias = Number.isFinite(plazo) && plazo > 0 ? plazo : 30;
      setForm((prev) => ({
        ...prev,
        customerId: orig.customerId || orig.customer?.id || '',
        date: hoy.toISOString().split('T')[0],
        dueDate: new Date(hoy.getTime() + dias * 86400000).toISOString().split('T')[0],
        lines: lineasDeLaOriginal(orig, prev.rectificationMode),
        irpfRate: parseFloat(orig.irpfRate ?? 0),
        paymentMethod: orig.paymentMethod || 'transferencia',
        budgetId: '',
        budgetIds: [],
        isIssued: true,
      }));
      setCustomerSearch(orig.customer?.name ? `${orig.customer.name}${orig.customer.nif ? ` (${orig.customer.nif})` : ''}` : '');
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error cargando la factura que se rectifica');
      navigate('/invoices');
    }
  };

  const loadRegistrosVf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/verifactu`);
      setRegistrosVf(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('[factura] no se pudieron leer sus registros VeriFactu', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (error) {
      modal.error('Error cargando clientes');
    }
  };

  const loadProducts = async () => {
    try {
      const res = await api.get('/products?isActive=true');
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      modal.error('Error cargando catálogo de productos');
    }
  };

  const loadBudgets = async () => {
    try {
      const res = await api.get('/budgets');
      setBudgets(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      modal.error('Error cargando presupuestos');
    }
  };

  const loadInvoice = async () => {
    try {
      const res = await api.get(`/invoices/${id}`);
      const inv = res.data;
      setForm({
        customerId: inv.customerId || inv.customer?.id || '',
        date: inv.date ? new Date(inv.date).toISOString().split('T')[0] : '',
        dueDate: inv.dueDate ? new Date(inv.dueDate).toISOString().split('T')[0] : '',
        lines: inv.lines?.length > 0 ? inv.lines.map(l => ({
          productId: l.productId ? String(l.productId) : '',
          description: l.description,
          servicePeriod: l.servicePeriod || '',
          isTextOnly: l.isTextOnly === true,
          quantity: parseFloat(l.quantity),
          unitPrice: parseFloat(l.unitPrice),
          discountRate: parseFloat(l.discountRate ?? 0),
          taxRate: parseFloat(l.taxRate),
        })) : [{ productId: '', description: '', quantity: 1, unitPrice: 0, discountRate: 0, taxRate: defaultTaxRate }],
        irpfRate: parseFloat(inv.irpfRate ?? defaultIrpfRate ?? 0),
        notes: inv.notes || '',
        paymentMethod: inv.paymentMethod || 'transferencia',
        invoicePrefix: inv.invoicePrefix || '',
        budgetId: (inv.budgetId || inv.budget?.id) ? String(inv.budgetId || inv.budget?.id) : '',
        budgetIds: Array.isArray(inv.budgetLinks) && inv.budgetLinks.length > 0
          ? inv.budgetLinks.map((link) => String(link.budget.id))
          : (inv.budgetId || inv.budget?.id) ? [String(inv.budgetId || inv.budget?.id)] : [],
        isIssued: inv.isIssued === true,
        isAccounted: inv.isAccounted === true,
        collectionStatus: inv.collectionStatus || 'pendiente',
        collectedAmount: Number(inv.collectedAmount || 0),
        sentToCustomerAt: inv.sentToCustomerAt || null,
        sentToAccountantAt: inv.sentToAccountantAt || null,
        cancelledAt: inv.cancelledAt || null,
        rectificationType: inv.rectificationType || '',
        rectificationMode: inv.rectificationMode || '',
        rectificationReason: inv.rectificationReason || ''
      });
      setLoadedInvoiceNumber(inv.invoiceNumber || '');
      setIsProforma(inv.isProforma === true);
      setConvertedAt(inv.convertedAt || null);
      setIsRectificativa(inv.isRectificativa === true);
      setRectificada(inv.rectifiedInvoice || null);
      setRectificativas(Array.isArray(inv.rectificativas) ? inv.rectificativas : []);
      if (!inv.isProforma) loadRegistrosVf();
      setCustomerSearch(inv.customer?.name ? `${inv.customer.name}${inv.customer.nif ? ` (${inv.customer.nif})` : ''}` : '');
      setBudgetSearch('');
      setBudgetCandidateId('');
    } catch (error) {
      modal.error('Error cargando factura');
      navigate('/invoices');
    }
  };

  const getCustomerDiscount = () => {
    const c = customers.find(c => String(c.id) === String(form.customerId));
    return c ? parseFloat(c.discountRate || 0) : 0;
  };

  const getCustomerDiscountById = (customerId) => {
    const customer = customers.find(c => String(c.id) === String(customerId));
    return customer ? parseFloat(customer.discountRate || 0) : 0;
  };

  const getCustomerLabel = (customer) => {
    if (!customer) return '';
    return `${customer.name}${customer.nif ? ` (${customer.nif})` : ''}`;
  };

  const getBudgetLabel = (budget) => {
    if (!budget) return '';
    const customer = customers.find((item) => String(item.id) === String(budget.customerId));
    return customer ? `${budget.fileName} — ${customer.name}` : budget.fileName;
  };

  const getLinesWithCustomerDiscount = (customerId, lines = form.lines) => {
    const discount = getCustomerDiscountById(customerId);
    // Las líneas de solo texto no llevan importes: el descuento no las toca.
    return lines.map((line) => (line.isTextOnly ? line : { ...line, discountRate: discount }));
  };

  const selectedBudgetIds = Array.isArray(form.budgetIds) ? form.budgetIds.map(String) : [];
  const selectedBudgets = selectedBudgetIds
    .map((budgetId) => budgets.find((budget) => String(budget.id) === String(budgetId)))
    .filter(Boolean);

  const addLine = () => {
    const discount = getCustomerDiscount();
    setForm({ ...form, lines: [...form.lines, { productId: '', description: '', isTextOnly: false, quantity: 1, unitPrice: 0, discountRate: discount, taxRate: defaultTaxRate }] });
  };

  // Línea de solo texto: se escribe el concepto y ya está. Sin cantidad, sin
  // precio y sin IVA; no suma nada a la factura.
  const addTextLine = () => {
    setForm({ ...form, lines: [...form.lines, { productId: '', description: '', isTextOnly: true, quantity: 0, unitPrice: 0, discountRate: 0, taxRate: 0 }] });
  };

  const addLineFromProduct = (product) => {
    const discount = getCustomerDiscount();
    const newLine = {
      productId: String(product.id),
      description: product.name || '',
      isTextOnly: false,
      quantity: 1,
      unitPrice: Number(product.unitPrice ?? 0),
      discountRate: discount,
      taxRate: Number(product.taxRate ?? defaultTaxRate),
    };
    setForm((prev) => ({ ...prev, lines: [...prev.lines, newLine] }));
    setCatalogOpen(false);
    setCatalogSearch('');
  };

  const catalogFiltered = catalogSearch.trim() === ''
    ? products
    : products.filter((p) => {
        const q = catalogSearch.toLowerCase();
        return (
          String(p.name || '').toLowerCase().includes(q) ||
          String(p.code || '').toLowerCase().includes(q) ||
          String(p.description || '').toLowerCase().includes(q)
        );
      });

  const handleCustomerChange = (customerId) => {
    const conflictingBudget = selectedBudgets.find((budget) => (
      budget.customerId && String(budget.customerId) !== String(customerId)
    ));

    if (conflictingBudget) {
      modal.error('Alguno de los presupuestos asociados pertenece a otro cliente. Quita ese presupuesto antes de cambiar el cliente.');
      return;
    }

    const customer = customers.find((item) => String(item.id) === String(customerId));
    setCustomerSearch(getCustomerLabel(customer));
    setForm({
      ...form,
      customerId,
      lines: getLinesWithCustomerDiscount(customerId)
    });
  };

  const addBudgetAssociation = () => {
    if (!budgetCandidateId) {
      modal.error('Selecciona un presupuesto para asociarlo.');
      return;
    }

    const selectedBudget = budgets.find((budget) => String(budget.id) === String(budgetCandidateId));
    if (!selectedBudget) {
      modal.error('El presupuesto seleccionado ya no existe.');
      return;
    }

    const conflictingBudget = selectedBudgets.find((budget) => (
      budget.customerId && selectedBudget.customerId && String(budget.customerId) !== String(selectedBudget.customerId)
    ));

    if (conflictingBudget) {
      modal.error('No puedes mezclar presupuestos de clientes distintos en la misma factura.');
      return;
    }

    const nextCustomerId = selectedBudget.customerId ? String(selectedBudget.customerId) : form.customerId;
    const nextBudgetIds = [...selectedBudgetIds, String(selectedBudget.id)];

    setForm({
      ...form,
      customerId: nextCustomerId,
      budgetId: nextBudgetIds[0] || '',
      budgetIds: nextBudgetIds,
      lines: nextCustomerId ? getLinesWithCustomerDiscount(nextCustomerId) : form.lines
    });
    setBudgetCandidateId('');
    setBudgetSearch('');

    if (selectedBudget.customerId && String(form.customerId) !== String(selectedBudget.customerId)) {
      const customer = customers.find((item) => String(item.id) === String(selectedBudget.customerId));
      setCustomerSearch(getCustomerLabel(customer));
      modal.info('Se ha ajustado el cliente al del presupuesto seleccionado.');
    }
  };

  const removeBudgetAssociation = (budgetIdToRemove) => {
    const nextBudgetIds = selectedBudgetIds.filter((budgetIdValue) => String(budgetIdValue) !== String(budgetIdToRemove));
    setForm({
      ...form,
      budgetId: nextBudgetIds[0] || '',
      budgetIds: nextBudgetIds
    });
  };

  const applyTemplate = (template) => {
    const discount = getCustomerDiscount();
    const nextLines = template.lines?.map((line) => (
      line.isTextOnly === true
        ? { description: line.description ?? '', isTextOnly: true, quantity: 0, unitPrice: 0, discountRate: 0, taxRate: 0 }
        : {
          description: line.description ?? '',
          isTextOnly: false,
          quantity: Number(line.quantity ?? 1),
          unitPrice: Number(line.unitPrice ?? 0),
          discountRate: Number(line.discountRate ?? discount),
          taxRate: Number(line.taxRate ?? defaultTaxRate),
        }
    ));

    if (!nextLines || nextLines.length === 0) {
      return;
    }

    setForm({ ...form, lines: nextLines });
  };

  const removeLine = (index) => {
    if (form.lines.length <= 1) return;
    setForm({ ...form, lines: form.lines.filter((_, i) => i !== index) });
  };

  const updateLine = (index, field, value) => {
    const lines = [...form.lines];
    lines[index] = { ...lines[index], [field]: value };
    setForm({ ...form, lines });
  };

  const calcSubtotal = () => {
    return form.lines.reduce((sum, line) => {
      const base = getLineBase(line);
      return sum + base;
    }, 0);
  };

  const calcTax = () => {
    return form.lines.reduce((sum, line) => {
      const base = getLineBase(line);
      return sum + (base * getDecimalValue(line.taxRate) / 100);
    }, 0);
  };

  const calcIrpf = () => {
    const subtotal = calcSubtotal();
    return subtotal * getDecimalValue(form.irpfRate) / 100;
  };

  const calcTotal = () => calcSubtotal() + calcTax() - calcIrpf();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const filteredCustomers = customers.filter((customer) => {
    if (String(customer.id) === String(form.customerId)) {
      return true;
    }

    const searchTerm = customerSearch.trim().toLowerCase();
    if (searchTerm === '') {
      return true;
    }

    const haystack = [
      customer.name,
      customer.nif,
      customer.email,
      customer.phone
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(searchTerm);
  });

  const filteredBudgets = budgets.filter((budget) => {
    if (selectedBudgetIds.includes(String(budget.id))) {
      return false;
    }

    const matchesCustomer = !budget.customerId || !form.customerId || String(budget.customerId) === String(form.customerId);
    const matchesSearch = budgetSearch.trim() === ''
      || budget.fileName.toLowerCase().includes(budgetSearch.toLowerCase());
    return matchesCustomer && matchesSearch;
  });

  const viewPdf = async (invoiceId) => {
    try {
      const pdfRes = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      modal.error('Error generando PDF');
    }
  };

  const downloadPdf = async (invoiceId) => {
    try {
      const pdfRes = await api.get(`/invoices/${invoiceId}/pdf/download`, { responseType: 'blob' });
      const disposition = pdfRes.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `Factura_${invoiceId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      modal.error('Error descargando PDF');
    }
  };

  const savePdf = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/pdf/save`);
      modal.success('✅ PDF generado y guardado correctamente');
    } catch (err) {
      modal.error('Error guardando PDF');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerId) return modal.error('Selecciona un cliente');
    if (!form.dueDate) return modal.error('Indica la fecha de vencimiento');
    const normalizedLines = form.lines.map((line) => (
      line.isTextOnly
        ? { ...line, isTextOnly: true, quantity: 0, unitPrice: 0, discountRate: 0, taxRate: 0 }
        : {
          ...line,
          isTextOnly: false,
          quantity: getDecimalValue(line.quantity),
          unitPrice: getDecimalValue(line.unitPrice),
          discountRate: getDecimalValue(line.discountRate),
          taxRate: getDecimalValue(line.taxRate),
        }
    ));
    // A una línea de solo texto se le pide el texto y nada más; a las demás,
    // cantidad y precio como siempre.
    if (normalizedLines.some((line) => !line.description)) {
      return modal.error('Escribe el texto de todos los conceptos');
    }
    // En una rectificativa las cantidades pueden ser negativas (por diferencias,
    // lo que baja); lo que no vale en ninguna es un concepto a cero.
    const conceptoMal = (line) => (esRectificativa
      ? (line.quantity === 0 || line.unitPrice === 0)
      : (line.quantity <= 0 || line.unitPrice <= 0));
    if (normalizedLines.some((line) => !line.isTextOnly && conceptoMal(line))) {
      return modal.error('Completa todos los conceptos correctamente');
    }
    if (creatingRectificativa && !String(form.rectificationReason || '').trim()) {
      return modal.error('Escribe el motivo de la rectificación');
    }

    const irpfRateGlobal = getDecimalValue(form.irpfRate);
    const subtotalFactura = normalizedLines.reduce((sum, line) => {
      const subtotalLinea = line.quantity * line.unitPrice * (1 - line.discountRate / 100);
      return sum + subtotalLinea + (subtotalLinea * line.taxRate / 100);
    }, 0);
    const totalFactura = subtotalFactura - (
      normalizedLines.reduce((sum, line) => sum + line.quantity * line.unitPrice * (1 - line.discountRate / 100), 0)
        * irpfRateGlobal / 100
    );

    let collectedAmount = 0;
    if (form.collectionStatus === 'parcial') {
      collectedAmount = getDecimalValue(form.collectedAmount);
      if (collectedAmount <= 0) {
        return modal.error('Indica una cantidad ingresada válida');
      }
      if (collectedAmount > totalFactura) {
        return modal.error('La cantidad ingresada no puede superar el total de la factura');
      }
    } else if (form.collectionStatus === 'cobrada') {
      collectedAmount = totalFactura;
    }

    setLoading(true);
    try {
      const normalizedBudgetIds = selectedBudgetIds
        .map((budgetIdValue) => parseInt(budgetIdValue, 10))
        .filter((budgetIdValue) => Number.isInteger(budgetIdValue) && budgetIdValue > 0);
      const payload = {
        ...form,
        customerId: parseInt(form.customerId, 10),
        budgetId: normalizedBudgetIds[0] || '',
        budgetIds: normalizedBudgetIds,
        collectedAmount,
        lines: normalizedLines,
        ...(creatingProforma ? { isProforma: true } : {}),
        ...(creatingRectificativa ? { isRectificativa: true, rectifiedInvoiceId: rectificada?.id, isIssued: true } : {})
      };
      if (id) {
        const res = await api.put(`/invoices/${id}`, payload);
        let msg = '✅ Datos de la factura guardados correctamente';
        if (res.data._pdfRegenerated) msg += '\nEl PDF se ha regenerado automáticamente.';
        if (res.data._pdfUploaded) msg += '\nEl PDF actualizado se ha enviado a la asesoría.';
        if (res.data._pdfUploadError) msg += `\n⚠️ No se pudo enviar a la asesoría: ${res.data._pdfUploadError}`;
        modal.success(msg);
      } else {
        const created = await api.post('/invoices', payload);
        modal.success(`${creatingProforma ? 'Proforma' : (creatingRectificativa ? 'Factura rectificativa' : 'Factura')} ${created.data?.invoiceNumber || ''} creada.`);
        navigate(creatingProforma ? '/proformas' : '/invoices');
        return;
      }
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando factura');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="invoice-form-shell">
      <div className="page-header" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#ffffff', paddingTop: 10, paddingBottom: 10, borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn-icon" onClick={() => navigate(isProforma ? '/proformas' : '/invoices', { state: { volverALaLista: true } })}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {id
                ? <>Editar {isProforma ? 'proforma' : (isRectificativa ? 'factura rectificativa' : 'factura')} {loadedInvoiceNumber && <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{loadedInvoiceNumber}</span>}</>
                : (creatingProforma ? 'Nueva proforma' : (creatingRectificativa ? 'Nueva factura rectificativa' : 'Nueva factura'))}
              {(isProforma || !id || form.isIssued) && (
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  background: esRectificativa ? 'var(--warning)' : (isProforma ? (convertedAt ? '#94a3b8' : '#2563eb') : (form.isIssued ? '#10b981' : '#f59e0b')),
                  color: esRectificativa ? 'var(--secondary)' : '#fff'
                }}>
                  {esRectificativa ? 'Rectificativa' : (isProforma ? (convertedAt ? 'Convertida' : 'Proforma') : (form.isIssued ? 'Emitida' : 'Borrador'))}
                </span>
              )}
            </h1>
            <p className="page-subtitle">
              {isProforma
                ? (convertedAt
                  ? 'Esta proforma ya se convirtió en factura y está bloqueada. Edita o rectifica la factura resultante.'
                  : 'Proforma sin validez fiscal y con numeración independiente. Editable hasta que la conviertas en factura desde el listado.')
                : creatingRectificativa
                  ? 'Corrige una factura ya expedida. Al guardarla se emite y se comunica a Hacienda por VeriFactu, y ya no se puede borrar.'
                  : id
                    ? 'Estás editando una factura existente. Los cambios se guardarán sobre esta misma factura.'
                    : 'Estás creando una factura nueva. Se guardará como borrador hasta que la marques como emitida.'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="invoice-form-layout">
        {!id && !creatingRectificativa && <InvoiceTemplates onApplyTemplate={applyTemplate} />}

        {esRectificativa && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 className="card-title" style={{ marginBottom: 12 }}>Rectificación</h2>
            {rectificada && (
              <p style={{ margin: '0 0 14px', color: 'var(--gray-600)' }}>
                Rectifica la factura <strong>N. {numeroImpreso(rectificada)}</strong> de {new Date(rectificada.date).toLocaleDateString('es-ES')}
                {rectificada.total !== undefined && <> · total {formatCurrency(Number(rectificada.total))}</>}
              </p>
            )}
            <div className="invoice-general-grid">
              <div className="form-group invoice-general-field invoice-general-field--wide">
                <label className="form-label">Motivo de la rectificación *</label>
                <textarea
                  className="form-input"
                  value={form.rectificationReason}
                  onChange={e => setForm({ ...form, rectificationReason: e.target.value })}
                  disabled={!creatingRectificativa}
                  maxLength={500}
                  placeholder="Por qué se corrige la factura. Sale en la rectificativa y se comunica a Hacienda."
                />
              </div>
              <div className="form-group invoice-general-field invoice-general-field--wide">
                <label className="form-label">Tipo *</label>
                <select className="form-input" value={form.rectificationType} onChange={e => setForm({ ...form, rectificationType: e.target.value })} disabled={!creatingRectificativa}>
                  <option value="R1">Devolución, descuento, operación que queda sin efecto o error de derecho (R1)</option>
                  <option value="R2">El cliente está en concurso de acreedores (R2)</option>
                  <option value="R3">Deuda incobrable (R3)</option>
                  <option value="R4">Cualquier otro error o corrección (R4)</option>
                </select>
              </div>
              <div className="form-group invoice-general-field invoice-general-field--wide">
                <label className="form-label">Método *</label>
                <select className="form-input" value={form.rectificationMode} onChange={e => setForm({ ...form, rectificationMode: e.target.value })} disabled={!creatingRectificativa}>
                  <option value="I">Por diferencias: los conceptos llevan solo lo que cambia</option>
                  <option value="S">Por sustitución: los conceptos llevan cómo queda la factura</option>
                </select>
              </div>
            </div>
            {creatingRectificativa && rectificada && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="invoice-search-help" style={{ margin: 0 }}>
                  {form.rectificationMode === 'S'
                    ? 'Por sustitución: los conceptos son la factura entera tal como debería haber sido.'
                    : 'Por diferencias: en negativo lo que baja y en positivo lo que sube. De partida se copian los conceptos de la factura en negativo, que la deshacen entera.'}
                </span>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setForm((prev) => ({ ...prev, lines: lineasDeLaOriginal(rectificada, prev.rectificationMode) }))}>
                  Volver a copiar los conceptos de la factura
                </button>
              </div>
            )}
          </div>
        )}

        <div className="card invoice-general-card" style={{ marginBottom: 20 }}>
          <h2 className="card-title" style={{ marginBottom: 20 }}>Datos generales</h2>
          <div className="invoice-general-grid">
            <div className="form-group invoice-general-field invoice-general-field--wide">
              <label className="form-label">Cliente *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Escribe para filtrar clientes..."
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <select className="form-input" value={form.customerId} onChange={e => handleCustomerChange(e.target.value)} required disabled={esRectificativa} title={esRectificativa ? 'Una rectificativa es del cliente de la factura que corrige' : undefined}>
                <option value="">Seleccionar cliente</option>
                {filteredCustomers.map(c => (
                  <option key={c.id} value={c.id}>{c.name} {c.nif ? `(${c.nif})` : ''}</option>
                ))}
              </select>
              <div className="invoice-search-help">
                {filteredCustomers.length} cliente{filteredCustomers.length === 1 ? '' : 's'} visible{filteredCustomers.length === 1 ? '' : 's'}
              </div>
            </div>
            {!esRectificativa && (
            <div className="form-group invoice-general-field invoice-general-field--wide">
              <label className="form-label">Presupuestos asociados</label>
              <input
                type="text"
                className="form-input"
                placeholder="Buscar presupuesto por nombre..."
                value={budgetSearch}
                onChange={e => setBudgetSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
              <div className="invoice-budget-picker">
                <select className="form-input" value={budgetCandidateId} onChange={e => setBudgetCandidateId(e.target.value)}>
                  <option value="">Seleccionar presupuesto para añadir</option>
                  {filteredBudgets.map((budget) => (
                    <option key={budget.id} value={budget.id}>
                      {getBudgetLabel(budget)}
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn-outline" onClick={addBudgetAssociation}>
                  <Plus size={16} /> Añadir presupuesto
                </button>
              </div>
              <div className="invoice-search-help">
                {filteredBudgets.length} presupuesto{filteredBudgets.length === 1 ? '' : 's'} disponible{filteredBudgets.length === 1 ? '' : 's'} para añadir
              </div>
              {selectedBudgets.length > 0 ? (
                <div className="invoice-budget-chip-list">
                  {selectedBudgets.map((budget) => (
                    <div key={budget.id} className="invoice-budget-chip">
                      <span>{getBudgetLabel(budget)}</span>
                      <button type="button" className="btn-icon" onClick={() => removeBudgetAssociation(budget.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="invoice-search-help">Sin presupuestos asociados.</div>
              )}
            </div>
            )}
            <div className="form-group invoice-general-field">
              <label className="form-label">Método de pago</label>
              <select className="form-input" value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            {!esRectificativa && (
            <div className="form-group invoice-general-field invoice-general-field--compact">
              <label className="form-label">Prefijo</label>
              <input className="form-input" value={form.invoicePrefix}
                onChange={e => setForm({ ...form, invoicePrefix: e.target.value })}
                placeholder="F" maxLength={10} />
            </div>
            )}
            <div className="form-group invoice-general-field">
              <label className="form-label">Fecha</label>
              <input type="date" className="form-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="form-group invoice-general-field">
              <label className="form-label">Fecha de vencimiento *</label>
              <input type="date" className="form-input" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} required />
            </div>
            <div className="form-group invoice-general-field">
              <label className="form-label">Estado de cobro</label>
              <select className="form-input" value={form.collectionStatus} onChange={e => setForm({ ...form, collectionStatus: e.target.value })}>
                <option value="pendiente">Pendiente de cobro</option>
                <option value="cobrada">Cobrada</option>
                {!esRectificativa && <option value="parcial">Cobrada en parte</option>}
              </select>
            </div>
            {form.collectionStatus === 'parcial' && (
              <div className="form-group invoice-general-field">
                <label className="form-label">Cantidad ingresada</label>
                <DecimalInput
                  value={form.collectedAmount}
                  onValueChange={(value) => setForm({ ...form, collectedAmount: value })}
                />
              </div>
            )}
            <div className="form-group invoice-general-field invoice-general-field--toggle">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.sentToCustomerAt}
                  onChange={e => setForm({ ...form, sentToCustomerAt: e.target.checked ? (form.sentToCustomerAt || new Date().toISOString()) : null })}
                  style={{ width: 22, height: 22, accentColor: '#2563eb', cursor: 'inherit' }}
                />
                <span className="form-label" style={{ margin: 0, fontSize: 15 }}>
                  Enviada al cliente
                  {form.sentToCustomerAt && (
                    <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6, fontSize: 13 }}>
                      ({new Date(form.sentToCustomerAt).toLocaleDateString('es-ES')})
                    </span>
                  )}
                </span>
              </label>
            </div>
            <div className="form-group invoice-general-field invoice-general-field--toggle">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.sentToAccountantAt}
                  onChange={e => setForm({ ...form, sentToAccountantAt: e.target.checked ? (form.sentToAccountantAt || new Date().toISOString()) : null })}
                  style={{ width: 22, height: 22, accentColor: '#2563eb', cursor: 'inherit' }}
                />
                <span className="form-label" style={{ margin: 0, fontSize: 15 }}>
                  Enviada a asesoría
                  {form.sentToAccountantAt && (
                    <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 6, fontSize: 13 }}>
                      ({new Date(form.sentToAccountantAt).toLocaleDateString('es-ES')})
                    </span>
                  )}
                </span>
              </label>
            </div>
            {!isProforma && (
              <div className="form-group invoice-general-field invoice-general-field--toggle">
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.isAccounted}
                    onChange={e => setForm({ ...form, isAccounted: e.target.checked })}
                    style={{ width: 22, height: 22, accentColor: '#2563eb', cursor: 'inherit' }}
                  />
                  <span className="form-label" style={{ margin: 0, fontSize: 15 }}>Contabilizada</span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <h2 className="card-title">Conceptos</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setCatalogOpen(true)}>
                <Package size={16} /> Añadir desde catálogo
              </button>
              <button type="button" className="btn btn-outline btn-sm" onClick={addLine}>
                <Plus size={16} /> Añadir línea
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={addTextLine}
                title="Línea de solo texto: sin cantidad, sin precio y sin IVA. No suma nada a la factura."
              >
                <Plus size={16} /> Añadir línea de texto
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <div className="invoice-item-row invoice-item-row--header" aria-hidden="true">
              <span>Concepto</span>
              <span>Cant.</span>
              <span>Precio unit.</span>
              <span>Dto. %</span>
              <span>IVA %</span>
              <span></span>
            </div>
          </div>

          {form.lines.map((line, index) => (
            <div key={index} className="invoice-item-row invoice-item-row--editable">
              <div className="invoice-item-field invoice-item-field--full">
                <span className="invoice-item-mobile-label">Concepto</span>
                <AutoGrowTextarea
                  className="form-input invoice-item-concept"
                  placeholder={line.isTextOnly ? 'Texto libre, sin importe' : 'Concepto a facturar'}
                  value={line.description}
                  onChange={e => updateLine(index, 'description', e.target.value)}
                  required
                />
                {!line.isTextOnly && (
                  <input
                    className="form-input"
                    placeholder="Fecha de prestación del servicio (opcional)"
                    value={line.servicePeriod || ''}
                    onChange={e => updateLine(index, 'servicePeriod', e.target.value)}
                    style={{ marginTop: 6, fontSize: '0.82rem' }}
                    title="Solo para prestaciones de servicio (clases, trabajos en casa del cliente). Texto libre, p. ej. 'del 3 al 18 de junio'. Aparece en el PDF solo si lo rellenas."
                  />
                )}
              </div>
              {line.isTextOnly ? (
                // Línea de solo texto: sin casillas de cantidad, precio,
                // descuento ni IVA. El hueco mantiene el botón de borrar en su
                // columna, igual que en las demás líneas.
                <div className="invoice-item-textonly-gap" aria-hidden="true" />
              ) : (
                <div className="invoice-item-compact-grid">
                  <div className="invoice-item-field">
                    <span className="invoice-item-mobile-label">Cant.</span>
                    <DecimalInput
                      value={line.quantity}
                      onValueChange={(value) => updateLine(index, 'quantity', value)}
                      allowNegative={esRectificativa}
                    />
                  </div>
                  <div className="invoice-item-field">
                    <span className="invoice-item-mobile-label">Precio unit.</span>
                    <DecimalInput
                      value={line.unitPrice}
                      onValueChange={(value) => updateLine(index, 'unitPrice', value)}
                      allowNegative={esRectificativa}
                    />
                  </div>
                  <div className="invoice-item-field">
                    <span className="invoice-item-mobile-label">Dto. %</span>
                    <DecimalInput
                      value={line.discountRate ?? 0}
                      onValueChange={(value) => updateLine(index, 'discountRate', value)}
                    />
                  </div>
                  <div className="invoice-item-field">
                    <span className="invoice-item-mobile-label">IVA %</span>
                    <DecimalInput
                      value={line.taxRate}
                      onValueChange={(value) => updateLine(index, 'taxRate', value)}
                    />
                  </div>
                </div>
              )}
              <div className="invoice-item-action">
                <button type="button" className="btn-icon" onClick={() => removeLine(index)} style={{ color: '#ef4444' }}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}

          <div style={{ borderTop: '2px solid #e2e8f0', marginTop: 20, paddingTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
              <div className="invoice-item-field" style={{ maxWidth: 200 }}>
                <label className="form-label" style={{ fontSize: '0.85rem' }}>Retención IRPF %</label>
                <DecimalInput
                  value={form.irpfRate}
                  onValueChange={(value) => setForm({ ...form, irpfRate: value })}
                />
              </div>
              <div style={{ textAlign: 'right' }}>
                {form.lines.some(l => getDecimalValue(l.discountRate) > 0) && (
                  <div style={{ fontSize: '0.85rem', color: '#f59e0b', marginBottom: 8 }}>
                    Descuento incluido en líneas
                  </div>
                )}
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 8 }}>Subtotal (tras dto.): <strong>{formatCurrency(calcSubtotal())}</strong></div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 8 }}>IVA: <strong>{formatCurrency(calcTax())}</strong></div>
                <div style={{ fontSize: '0.95rem', color: '#0f172a', marginBottom: 8 }}>Total (base + IVA): <strong>{formatCurrency(calcSubtotal() + calcTax())}</strong></div>
                {(esRectificativa ? calcIrpf() !== 0 : calcIrpf() > 0) && (
                  <div style={{ fontSize: '0.85rem', color: '#dc2626', marginBottom: 8 }}>Retención IRPF (-): <strong>{formatCurrency(calcIrpf())}</strong></div>
                )}
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>Total: {formatCurrency(calcTotal())}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea className="form-input" placeholder="Notas adicionales para la factura..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>

        {id && !isProforma && (
          <div className="card" style={{ marginBottom: 20 }}>
            <h2 className="card-title" style={{ marginBottom: 12 }}>VeriFactu</h2>
            {rectificativas.length > 0 && (
              <p style={{ margin: '0 0 12px' }}>
                Rectificada por: {rectificativas.map((r, i) => (
                  <React.Fragment key={r.id}>
                    {i > 0 && ', '}
                    <a href={`/invoices/edit/${r.id}`} onClick={(e) => { e.preventDefault(); navigate(`/invoices/edit/${r.id}`); }} style={{ color: 'var(--primary)', fontWeight: 600 }}>{r.invoiceNumber}</a>
                  </React.Fragment>
                ))}
              </p>
            )}
            {registrosVf.length === 0 ? (
              <p className="invoice-search-help" style={{ margin: 0 }}>Esta factura no tiene registros VeriFactu: es anterior a VeriFactu o un borrador sin emitir.</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr><th>Registro</th><th>Generado</th><th>Estado</th><th>Justificante</th><th>Respuesta de Hacienda</th></tr>
                  </thead>
                  <tbody>
                    {registrosVf.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {r.tipo === 'alta' ? 'Alta' : 'Anulación'} {r.numSerie}
                          {r.subsanacion === 'S' && ' · rehecha tras rechazo'}
                          {r.sinRegistroPrevio === 'S' && ' · sin registro previo'}
                          {r.tipo === 'anulacion' && r.rechazoPrevio === 'S' && ' · tras rechazo'}
                          {r.mode === 'preprod' && ' · entorno de pruebas'}
                        </td>
                        <td className="dt-nowrap">{r.fechaHoraGen ? new Date(r.fechaHoraGen).toLocaleString('es-ES') : new Date(r.createdAt).toLocaleString('es-ES')}</td>
                        <td className="dt-nowrap">{ESTADOS_VF[r.status] || r.status}</td>
                        <td>{r.csvAeat || '—'}</td>
                        <td>{[r.responseCodigo, r.responseDescripcion || r.lastError].filter(Boolean).join(' · ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate(isProforma ? '/proformas' : '/invoices', { state: { volverALaLista: true } })}>Cancelar</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || (isProforma && !!convertedAt)}
            title={isProforma && convertedAt ? 'Proforma convertida: bloqueada. Trabaja sobre la factura resultante.' : undefined}
          >
            <Save size={16} /> {loading ? 'Guardando...' : (id ? 'Guardar datos' : (creatingProforma ? 'Crear proforma' : (creatingRectificativa ? 'Emitir rectificativa' : 'Crear factura')))}
          </button>
          {id && (
            <>
              <button type="button" className="btn btn-outline" onClick={() => savePdf(id)} disabled={loading}>
                <Save size={16} /> Guardar PDF
              </button>
              <button type="button" className="btn btn-outline" onClick={() => viewPdf(id)} disabled={loading}>
                <Eye size={16} /> Ver PDF
              </button>
              <button type="button" className="btn btn-outline" onClick={() => downloadPdf(id)} disabled={loading}>
                <Download size={16} /> Descargar PDF
              </button>
            </>
          )}
        </div>
      </form>

      {catalogOpen && (
        <div className="modal-overlay" onClick={() => setCatalogOpen(false)}>
          <div
            className="app-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560, width: '92%', textAlign: 'left' }}
          >
            <button className="app-modal-close" onClick={() => setCatalogOpen(false)} aria-label="Cerrar">
              <X size={18} />
            </button>
            <h3 style={{ marginTop: 0, marginBottom: 14, fontSize: '1.05rem' }}>Añadir desde catálogo</h3>
            <input
              type="text"
              autoFocus
              className="form-input"
              placeholder="Buscar por nombre, código o descripción..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {catalogFiltered.length === 0 && (
                <div style={{ padding: 22, textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                  {products.length === 0
                    ? 'Catálogo vacío. Crea artículos en Comercial → Productos.'
                    : 'Ningún artículo coincide con la búsqueda.'}
                </div>
              )}
              {catalogFiltered.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => addLineFromProduct(product)}
                  style={{
                    textAlign: 'left',
                    border: '1px solid var(--gray-200)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{product.name}</div>
                  {product.description && (
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{product.description}</div>
                  )}
                  <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                    {Number(product.unitPrice ?? 0).toFixed(2)} € · IVA {Number(product.taxRate ?? defaultTaxRate).toFixed(0)} %
                    {product.code ? ` · ${product.code}` : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceForm;
